import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface BrowserChromeTintProps {
  /** CSS renk değeri; `var(--sidebar-color, #102648)` gibi bir değişken de olabilir. */
  color: string;
}

/**
 * Tarayıcı arayüzünü (Safari sekme/araç çubuğu, Android durum çubuğu) uygulamanın
 * rengiyle boyayan görünmez şerit.
 *
 * Safari 26 `<meta name="theme-color">` etiketini yok sayıyor; onun yerine
 * viewport'un üst kenarındaki `position: fixed` bir elemanın arka plan rengini
 * örnekliyor. Eleman en az 3px yüksekliğinde ve macOS'ta pencerenin %90'ı,
 * iOS'ta %80'i kadar geniş olmalı. Layout'lardaki AppBar sabit olsa da genişliği
 * `calc(100% - kenar çubuğu)` olduğundan pencere genişliğine ve menünün açık/kapalı
 * durumuna göre bu eşiğin altına düşebiliyor; o durumda Safari body'nin beyazına
 * dönüyor ve renk ekrandan ekrana değişiyormuş gibi görünüyor. Bu şerit her koşulda
 * eşiği geçtiği için renk tutarlı kalır.
 *
 * Şerit AppBar'ın arkasında durur ve onunla aynı rengi kullandığı için görünmez.
 * theme-color etiketi de aynı renge çekilir: Safari 15-18 ve Chrome/Android hâlâ
 * bu etiketi kullanıyor.
 */
const BrowserChromeTint: React.FC<BrowserChromeTintProps> = ({ color }) => {
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;

    const sync = () => {
      if (!stripRef.current) return;
      // getComputedStyle, var() referansını gerçek renge çözer.
      const resolved = window.getComputedStyle(stripRef.current).backgroundColor;
      if (resolved) meta.setAttribute('content', resolved);
    };

    sync();

    // Kenar çubuğu ayarlarından renk değiştirildiğinde --sidebar-color
    // documentElement'in style'ına yazılıyor; etiketi de o an güncelliyoruz.
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, [color]);

  return (
    <Box
      ref={stripRef}
      aria-hidden
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: 6,
        backgroundColor: color,
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default BrowserChromeTint;
